<template>
    <v-card flat class="resource-pool-option-list d-flex flex-column">
        <v-list nav dense class="flex-grow-0 pb-0">
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-text-field
                        v-model="search"
                        placeholder="Filter resource pools..."
                        prepend-inner-icon="mdi-magnify"
                        background-color="grey lighten-4"
                        :autofocus="autofocus"
                        hide-details
                        solo
                        flat
                        dense
                        clearable>
                        <template v-if="(showTypeFilter && availablePoolTypes.length > 1) || availableRoles.length > 1" v-slot:append>
                            <PoolTypeFilterMenu
                                v-model="selectedPoolTypes"
                                :available-types="showTypeFilter ? availablePoolTypes : []"
                                :roles.sync="selectedRoles"
                                :available-roles="availableRoles" />
                        </template>
                    </v-text-field>
                </v-list-item-title>
            </v-list-item>

            <v-list-item>
                <v-list-item-title class="text-uppercase">Resource pools</v-list-item-title>
            </v-list-item>
        </v-list>

        <v-sheet class="flex-grow-1" style="min-height: 0">
            <v-list nav dense class="pt-0 pb-1">
                <v-list-item v-if="loading">
                    <v-list-item-content>
                        <v-progress-linear indeterminate rounded />
                    </v-list-item-content>
                </v-list-item>

                <v-virtual-scroll
                    v-else-if="visibleItems.length"
                    ref="virtualScroll"
                    :items="visibleItems"
                    :height="Math.min(visibleItems.length, 6) * 60"
                    :bench="2"
                    item-height="60"
                    class="resource-pool-scroll">
                    <template v-slot:default="{ item: entry }">
                        <v-list-item
                            :key="entry.item.rpid"
                            class="resource-pool-option"
                            :input-value="isSelected(entry.item)"
                            active-class="primary--text"
                            @click="$emit('select', entry.item)">
                            <v-list-item-icon class="mr-3">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on">{{ resourcePoolIcon(entry.item) }}</v-icon>
                                    </template>
                                    <span>{{ resourcePoolTypeLabel(entry.item) }}</span>
                                </v-tooltip>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title :class="entry.item.deleted && 'deleted'">{{ entry.item.long_id || entry.item.name }}</v-list-item-title>
                                <v-list-item-subtitle v-if="entry.metadata.hierarchyLabel || Number(entry.item.descendant_count)">
                                    <template v-if="entry.metadata.hierarchyLabel">{{ entry.metadata.hierarchyLabel }}</template>
                                    <template v-if="Number(entry.item.descendant_count)">
                                        <span v-if="entry.metadata.hierarchyLabel" class="mx-1">·</span>
                                        {{ entry.item.descendant_count }} {{ Number(entry.item.descendant_count) === 1 ? 'descendant' : 'descendants' }}
                                    </template>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action v-if="showRole && entry.metadata.role">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">
                                            {{ resourcePoolRoleMetadata(entry.metadata.role).icon }}
                                        </v-icon>
                                    </template>
                                    <span>{{ resourcePoolRoleMetadata(entry.metadata.role).label }}</span>
                                </v-tooltip>
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                </v-virtual-scroll>

                <v-list-item v-else>
                    <v-list-item-content>
                        <v-list-item-title class="text--disabled text-center">No matching resource pools found.</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-sheet>
    </v-card>
</template>

<script>
import PoolTypeFilterMenu from '@/modules/user/components/PoolTypeFilterMenu.vue'
import {
    resourcePoolRoleMetadata,
    RESOURCE_POOL_FILTER_TYPES,
    RESOURCE_POOL_ROLES,
    resourcePoolRole,
    resourcePoolRoleLabel,
    isSameResourcePoolId,
    resourcePoolFilterType,
    resourcePoolIcon,
    resourcePoolTypeLabel
} from '@/modules/user/components/resourcePoolMeta'

export default {
    name: 'ResourcePoolOptionList',
    components: { PoolTypeFilterMenu },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        selectedId: {
            type: [Number, String],
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        showTypeFilter: {
            type: Boolean,
            default: true
        },
        showRole: {
            type: Boolean,
            default: true
        },
        autofocus: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            search: '',
            selectedPoolTypes: RESOURCE_POOL_FILTER_TYPES.slice(),
            selectedRoles: RESOURCE_POOL_ROLES.slice()
        }
    },
    computed: {
        poolMetadataById() {
            const itemsById = new Map(this.items.map(item => [String(item.rpid), item]))
            const metadataById = new Map()

            this.items.forEach(item => {
                const filterType = resourcePoolFilterType(item)
                const parent = itemsById.get(String(item.parent_rpid))
                const root = itemsById.get(String(item.root_rpid))

                let hierarchyLabel = ''
                if (item.parent_long_id || parent?.long_id) {
                    hierarchyLabel = `Parent: ${item.parent_long_id || parent.long_id}`
                } else if (!isSameResourcePoolId(item.root_rpid, item.rpid) && (item.root_long_id || root?.long_id)) {
                    hierarchyLabel = `Root: ${item.root_long_id || root.long_id}`
                }

                const role = resourcePoolRole(item, item.is_rp_manager === false ? 'member' : null)
                const searchText = [
                    this.showRole ? resourcePoolRoleLabel(item, role) : '',
                    item.long_id,
                    item.name,
                    resourcePoolTypeLabel(item),
                    hierarchyLabel,
                    item.parent_long_id,
                    item.root_long_id,
                    item.descendant_count
                ]
                    .filter(value => value !== null && value !== undefined)
                    .map(value => String(value).toLowerCase())
                    .join(' ')

                metadataById.set(String(item.rpid), {
                    filterType,
                    role,
                    hierarchyLabel,
                    searchText
                })
            })

            return metadataById
        },
        availablePoolTypes() {
            return Array.from(new Set(Array.from(this.poolMetadataById.values(), metadata => metadata.filterType).filter(Boolean)))
        },
        availableRoles() {
            return this.showRole ? Array.from(new Set(Array.from(this.poolMetadataById.values(), metadata => metadata.role).filter(Boolean))) : []
        },
        visibleItems() {
            const query = String(this.search || '')
                .trim()
                .toLowerCase()

            return this.items
                .map(item => {
                    const metadata = this.poolMetadataById.get(String(item.rpid))
                    return {
                        item,
                        metadata
                    }
                })
                .filter(
                    entry =>
                        entry.metadata &&
                        this.selectedPoolTypes.includes(entry.metadata.filterType) &&
                        (!this.showRole || !entry.metadata.role || this.selectedRoles.includes(entry.metadata.role)) &&
                        (!query || entry.metadata.searchText.includes(query))
                )
        }
    },
    watch: {
        visibleItems() {
            this.$nextTick(() => {
                const scroller = this.$refs.virtualScroll
                if (scroller) {
                    scroller.$el.scrollTop = 0
                    scroller.onScroll()
                }
            })
        },
        items() {
            this.selectedPoolTypes = this.selectedPoolTypes.filter(type => RESOURCE_POOL_FILTER_TYPES.includes(type))
        }
    },
    methods: {
        resourcePoolRoleMetadata,
        resourcePoolIcon,
        resourcePoolTypeLabel,
        isSelected(pool) {
            return isSameResourcePoolId(this.selectedId, pool.rpid)
        },
        reset() {
            this.search = ''
            this.selectedPoolTypes = RESOURCE_POOL_FILTER_TYPES.slice()
            this.selectedRoles = RESOURCE_POOL_ROLES.slice()
        }
    }
}
</script>

<style scoped>
.resource-pool-option-list {
    max-height: 60vh;
    overflow: hidden;
}

.resource-pool-scroll {
    max-height: calc(60vh - 104px);
}

.resource-pool-option {
    height: 56px;
    margin-bottom: 4px;
}

.deleted {
    text-decoration: line-through;
    opacity: 0.5;
}
</style>
